import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import StockMemorialsSection from '../components/StockMemorialsSection';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Trash2, Package, Ship, Check, X, Clock, Search,
  FileText, DollarSign, AlertTriangle, CheckCircle, Truck,
  Calendar, Building2, Eye, RotateCcw, LinkIcon
} from 'lucide-react';

// Memorial types for initial selection
const MEMORIAL_TYPES = [
  { value: 'headstone', label: 'Headstone (with Base)', description: 'Standard headstone and base' },
  { value: 'cremation_tablet', label: 'Cremation Tablet', description: 'Small tablet memorial' },
  { value: 'cremation_desk', label: 'Cremation Desk', description: 'Desk-style cremation memorial' },
  { value: 'other', label: 'Other', description: 'Custom configuration' }
];

// Default sizes for memorial types (in inches)
const DEFAULT_SIZES = {
  headstone: { height: 27, width: 21, depth: 3 },
  base: { height: 3, width: 24, depth: 12 },
  cremation_tablet: { height: 12, width: 12, depth: 2 },
  cremation_desk: { height: 16, width: 20, depth: 12 }
};

// Predefined stone components
const COMPONENT_TYPES = [
  { value: 'headstone', label: 'Headstone' },
  { value: 'base', label: 'Base' },
  { value: 'vase', label: 'Vase' },
  { value: 'kerb', label: 'Kerbs' },
  { value: 'desk', label: 'Desk' },
  { value: 'book', label: 'Book' },
  { value: 'heart', label: 'Heart' },
  { value: 'tablet', label: 'Tablet' },
  { value: 'plaque', label: 'Plaque' },
  { value: 'custom', label: 'Custom (specify)' }
];

// Stone colours
const STONE_COLOURS = [
  'Black', 'Grey', 'Dark Grey', 'Light Grey', 'White', 'Blue Pearl',
  'Bahama Blue', 'Emerald Pearl', 'Ruby Red', 'Imperial Red', 'Aurora',
  'Paradiso', 'Vizag Blue', 'Tan Brown', 'Baltic Brown', 'Other'
];

// Stone finishes
const STONE_FINISHES = [
  'Polished', 'Honed', 'Rustic', 'Flamed', 'Sandblasted', 'Other'
];

// Status badge styling
const STATUS_STYLES = {
  'submitted': { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Submitted' },
  'quoted': { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Quote Received' },
  'accepted': { bg: 'bg-green-100', text: 'text-green-700', label: 'Accepted' },
  'ordered': { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Ordered' },
  'shipped': { bg: 'bg-cyan-100', text: 'text-cyan-700', label: 'Shipped' },
  'in_transit': { bg: 'bg-teal-100', text: 'text-teal-700', label: 'In Transit' },
  'arrived': { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Arrived' },
  'ready_for_collection': { bg: 'bg-lime-100', text: 'text-lime-700', label: 'Ready for Collection' },
  'dispatched': { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Dispatched' },
  'collected': { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Collected' },
  'cancelled': { bg: 'bg-red-100', text: 'text-red-700', label: 'Cancelled' }
};

function GroupOrdering() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, API } = useContext(AuthContext);
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Track if this order is linked to a memorial order
  const [linkedMemorialOrder, setLinkedMemorialOrder] = useState(null);

  // Search + status filter for the group orders list
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | ready | awaiting | ordered | delivered
  
  // Form state
  const [form, setForm] = useState({
    job_name: '',
    memorial_type: '',
    stone_colour: '',
    custom_colour: '',
    stone_type: 'Granite',
    stone_finish: 'Polished',
    components: [],
    set_quantity: 1,
    customer_notes: '',
    order_id: '',
    order_number: '',
    request_date: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, ' ')
  });

  // Dispatch dialog state
  const [showDispatchDialog, setShowDispatchDialog] = useState(false);
  const [dispatchForm, setDispatchForm] = useState({
    delivery_method: 'collect', // 'collect' or 'pallet_delivery'
    pallet_count: 1,
    pallet_cost: 70
  });
  const [dispatchingOrder, setDispatchingOrder] = useState(null);

  useEffect(() => {
    fetchOrders();
    
    // Check if we're coming from a memorial order
    const fromOrder = searchParams.get('from_order');
    if (fromOrder === 'true') {
      const prefillData = sessionStorage.getItem('chinaOrderPrefill');
      if (prefillData) {
        try {
          const data = JSON.parse(prefillData);
          setLinkedMemorialOrder({
            order_id: data.order_id,
            order_number: data.order_number,
            client_name: data.client_name
          });
          setForm(prev => ({
            ...prev,
            job_name: data.job_name || '',
            stone_colour: data.stone_colour || '',
            stone_type: data.stone_type || 'Granite',
            order_id: data.order_id || '',
            order_number: data.order_number || ''
          }));
          // Clear the session storage
          sessionStorage.removeItem('chinaOrderPrefill');
          // Open the create dialog
          setCreateDialog(true);
          toast.info(`Creating group order for Memorial Order #${data.order_number}`);
        } catch (e) {
          console.error('Failed to parse prefill data:', e);
        }
      }
    }
  }, [searchParams]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/group-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch group orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      job_name: '',
      memorial_type: '',
      stone_colour: '',
      custom_colour: '',
      stone_type: 'Granite',
      stone_finish: 'Polished',
      components: [],
      set_quantity: 1,
      customer_notes: '',
      order_id: '',
      order_number: '',
      request_date: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, ' ')
    });
    setLinkedMemorialOrder(null);
  };

  // Handle memorial type selection - auto-populate components with defaults
  const handleMemorialTypeChange = (type) => {
    let newComponents = [];
    
    if (type === 'headstone') {
      // Headstone with base - both pre-filled with default sizes
      newComponents = [
        { 
          component_type: 'headstone', 
          custom_description: '', 
          height: DEFAULT_SIZES.headstone.height.toString(), 
          width: DEFAULT_SIZES.headstone.width.toString(), 
          depth: DEFAULT_SIZES.headstone.depth.toString(), 
          quantity: 1, 
          notes: '' 
        },
        { 
          component_type: 'base', 
          custom_description: '', 
          height: DEFAULT_SIZES.base.height.toString(), 
          width: DEFAULT_SIZES.base.width.toString(), 
          depth: DEFAULT_SIZES.base.depth.toString(), 
          quantity: 1, 
          notes: '' 
        }
      ];
    } else if (type === 'cremation_tablet') {
      newComponents = [
        { 
          component_type: 'tablet', 
          custom_description: '', 
          height: DEFAULT_SIZES.cremation_tablet.height.toString(), 
          width: DEFAULT_SIZES.cremation_tablet.width.toString(), 
          depth: DEFAULT_SIZES.cremation_tablet.depth.toString(), 
          quantity: 1, 
          notes: '' 
        }
      ];
    } else if (type === 'cremation_desk') {
      newComponents = [
        { 
          component_type: 'desk', 
          custom_description: '', 
          height: DEFAULT_SIZES.cremation_desk.height.toString(), 
          width: DEFAULT_SIZES.cremation_desk.width.toString(), 
          depth: DEFAULT_SIZES.cremation_desk.depth.toString(), 
          quantity: 1, 
          notes: '' 
        }
      ];
    } else if (type === 'other') {
      // Start with empty component for custom entry
      newComponents = [
        { component_type: 'custom', custom_description: '', height: '', width: '', depth: '', quantity: 1, notes: '' }
      ];
    }
    
    setForm(prev => ({
      ...prev,
      memorial_type: type,
      components: newComponents
    }));
  };

  const addComponent = () => {
    setForm(prev => ({
      ...prev,
      components: [...prev.components, { component_type: 'base', custom_description: '', height: '', width: '', depth: '', quantity: 1, notes: '' }]
    }));
  };

  const removeComponent = (index) => {
    if (form.components.length <= 1) {
      toast.error('At least one component is required');
      return;
    }
    setForm(prev => ({
      ...prev,
      components: prev.components.filter((_, i) => i !== index)
    }));
  };

  const updateComponent = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      components: prev.components.map((comp, i) => 
        i === index ? { ...comp, [field]: value } : comp
      )
    }));
  };

  const handleSubmit = async () => {
    // Validation
    const colour = form.stone_colour === 'Other' ? form.custom_colour : form.stone_colour;
    if (!colour) {
      toast.error('Please select a stone colour');
      return;
    }
    
    const invalidComponents = form.components.filter(c => 
      !c.component_type || 
      (c.component_type === 'custom' && !c.custom_description) ||
      !c.height || !c.width || !c.depth
    );
    
    if (invalidComponents.length > 0) {
      toast.error('Please fill in all dimensions for each component');
      return;
    }
    
    setSubmitting(true);
    try {
      const setMultiplier = Math.max(1, parseInt(form.set_quantity, 10) || 1);
      const payload = {
        job_name: form.job_name || null,
        stone_colour: colour,
        stone_type: form.stone_type,
        stone_finish: form.stone_finish,
        components: form.components.map(c => ({
          component_type: c.component_type,
          custom_description: c.component_type === 'custom' ? c.custom_description : null,
          height: parseFloat(c.height),
          width: parseFloat(c.width),
          depth: parseFloat(c.depth),
          quantity: (parseInt(c.quantity) || 1) * setMultiplier,
          notes: c.notes || null
        })),
        customer_notes: setMultiplier > 1
          ? `Number of sets: ${setMultiplier}.${form.customer_notes ? ' ' + form.customer_notes : ''}`
          : (form.customer_notes || null),
        order_id: form.order_id || null,
        order_number: form.order_number || null,
        request_date: form.request_date
      };
      
      const response = await axios.post(`${API}/group-orders`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // If this was linked to a memorial order, update its status to stone-ordered
      if (linkedMemorialOrder?.order_id) {
        try {
          await axios.patch(`${API}/orders/${linkedMemorialOrder.order_id}`, {
            status: 'stone-ordered',
            status_date: new Date().toISOString()
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success(`Memorial Order #${linkedMemorialOrder.order_number} marked as Stone Ordered!`);
        } catch (statusError) {
          console.error('Failed to update memorial order status:', statusError);
          toast.warning('Group order created but failed to update memorial order status');
        }
      }
      
      toast.success(`Order submitted! Reference: ${response.data.reference_number}`);
      setCreateDialog(false);
      resetForm();
      fetchOrders();
    } catch (error) {
      console.error('Failed to submit order:', error);
      let errorMessage = 'Failed to submit order';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || detail.message || JSON.stringify(detail);
        }
      }
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAcceptQuote = async (orderId) => {
    try {
      await axios.post(`${API}/group-orders/${orderId}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Quote accepted! You will receive a deposit invoice shortly.');
      fetchOrders();
      setViewDialog(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to accept quote');
      toast.error(msg);
    }
  };

  const handleDeclineQuote = async (orderId) => {
    if (!window.confirm('Are you sure you want to decline this quote?')) return;
    try {
      await axios.post(`${API}/group-orders/${orderId}/decline`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Quote declined');
      fetchOrders();
      setViewDialog(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to decline quote');
      toast.error(msg);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order request?')) return;
    try {
      await axios.delete(`${API}/group-orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Order deleted');
      fetchOrders();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to delete order');
      toast.error(msg);
    }
  };

  const handleCancelOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to cancel this order? This action cannot be undone.')) return;
    try {
      await axios.post(`${API}/group-orders/${orderId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Order cancelled successfully');
      fetchOrders();
      setViewDialog(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to cancel order');
      toast.error(msg);
    }
  };

  // Reorder from a previous order - copies stone details and components
  const handleReorder = (order) => {
    // Determine memorial type based on components
    let memorialType = '';
    const hasBase = order.components?.some(c => c.component_type === 'base');
    const hasHeadstone = order.components?.some(c => c.component_type === 'headstone');
    const hasKerbs = order.components?.some(c => c.component_type === 'kerbs');
    
    if (hasHeadstone && hasBase && hasKerbs) {
      memorialType = 'full_memorial';
    } else if (hasHeadstone && hasBase) {
      memorialType = 'headstone_base';
    } else if (hasHeadstone) {
      memorialType = 'headstone_only';
    }
    
    // Copy form data from the order
    setForm({
      job_name: '', // Clear job name - user should give new name
      memorial_type: memorialType,
      stone_colour: order.stone_colour || '',
      custom_colour: '',
      stone_type: order.stone_type || 'Granite',
      stone_finish: order.stone_finish || 'Polished',
      components: order.components?.map(c => ({
        ...c,
        id: Date.now() + Math.random() // New IDs for components
      })) || [],
      customer_notes: '',
      order_id: '',
      order_number: '',
      request_date: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, ' ')
    });
    
    // Open the create dialog
    setCreateDialog(true);
    setViewDialog(false);
    toast.success('Order details copied - ready to submit as new order');
  };

  // Handle setting up dispatch
  const handleOpenDispatch = (order) => {
    setDispatchingOrder(order);
    setDispatchForm({
      delivery_method: order.delivery_method || 'collect',
      pallet_count: order.pallet_count || 1,
      pallet_cost: order.pallet_cost || 70
    });
    setShowDispatchDialog(true);
  };

  const handleConfirmDispatch = async () => {
    if (!dispatchingOrder) return;
    
    try {
      const payload = {
        delivery_method: dispatchForm.delivery_method,
        pallet_delivery: dispatchForm.delivery_method === 'pallet_delivery',
        pallet_count: dispatchForm.delivery_method === 'pallet_delivery' ? parseInt(dispatchForm.pallet_count) || 1 : 0,
        pallet_cost: parseFloat(dispatchForm.pallet_cost) || 70,
        status: dispatchForm.delivery_method === 'collect' ? 'ready_for_collection' : 'dispatched'
      };
      
      await axios.put(`${API}/group-orders/${dispatchingOrder.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const methodLabel = dispatchForm.delivery_method === 'collect' ? 'Ready for Collection' : 'Pallet Delivery Arranged';
      toast.success(`Order marked as ${methodLabel}`);
      setShowDispatchDialog(false);
      setDispatchingOrder(null);
      fetchOrders();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to update dispatch details');
      toast.error(msg);
    }
  };

  // Check if user can cancel (before production starts)
  const canUserCancel = (status) => {
    const preProductionStatuses = ['submitted', 'quoted', 'accepted'];
    return preProductionStatuses.includes(status);
  };

  // Check if order can be deleted (submitted or cancelled)
  const canDelete = (status) => {
    return ['submitted', 'cancelled'].includes(status);
  };

  // Check if user is admin (can cancel any order)
  const isAdmin = user?.role === 'super_admin' || user?.company_id === 'set-in-stone';

  const openViewDialog = (order) => {
    setSelectedOrder(order);
    setViewDialog(true);
  };

  const handleMarkCollected = async (orderId) => {
    if (!window.confirm('Mark this order as collected by the customer?')) return;
    
    try {
      await axios.put(`${API}/group-orders/${orderId}`, {
        status: 'collected',
        collected_at: new Date().toISOString()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Order marked as collected');
      fetchOrders();
      setViewDialog(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to update order');
      toast.error(msg);
    }
  };

  const getStatusBadge = (status) => {
    const style = STATUS_STYLES[status] || STATUS_STYLES['submitted'];
    return (
      <Badge className={`${style.bg} ${style.text}`}>
        {style.label}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return `£${parseFloat(amount).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`;
  };

  // Separate orders by status
  // Apply search + tab filter on top of the raw orders list.
  const matchesSearch = (o) => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      (o.reference_number || '').toLowerCase().includes(q) ||
      (o.job_name || '').toLowerCase().includes(q) ||
      (o.stone_colour || '').toLowerCase().includes(q) ||
      (o.order_number || '').toLowerCase().includes(q) ||
      (o.company_name || '').toLowerCase().includes(q)
    );
  };
  const statusMatchesFilter = (o) => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'awaiting') return ['submitted', 'quoted'].includes(o.status);
    if (statusFilter === 'ready')    return ['accepted'].includes(o.status);
    if (statusFilter === 'ordered')  return ['ordered', 'shipped', 'in_transit', 'arrived', 'ready_for_collection'].includes(o.status);
    if (statusFilter === 'delivered') return ['dispatched', 'collected'].includes(o.status);
    return true;
  };
  const visibleOrders = orders.filter(o => matchesSearch(o) && statusMatchesFilter(o));
  const pendingOrders   = visibleOrders.filter(o => ['submitted', 'quoted'].includes(o.status));
  const activeOrders    = visibleOrders.filter(o => ['accepted', 'ordered', 'shipped', 'in_transit', 'arrived', 'ready_for_collection', 'dispatched'].includes(o.status));
  const completedOrders = visibleOrders.filter(o => ['collected', 'cancelled'].includes(o.status));

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-8">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                  <Ship className="w-6 h-6 text-blue-600" />
                  Trade Wholesale
                </h1>
                <p className="text-sm text-slate-500">B2B trade portal — yard stock, incoming containers and bulk supplier orders</p>
              </div>
            </div>
            <Button onClick={() => setCreateDialog(true)} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              New Order Request
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-8">
        {/* ============== SECTION 1: Featured Stock (top) ============== */}
        <StockMemorialsSection onOrderPlaced={fetchOrders} />

        {/* ============== SECTION 2: Group Stone Orders ============== */}
        <section className="space-y-4" data-testid="group-orders-section">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 grid place-items-center text-white shadow-sm">
                <Ship className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-900">Group Stone Orders</h2>
                <p className="text-sm text-slate-500">Batched trade quotes — submitted, accepted and tracked here.</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search ref, job, colour…"
                  className="pl-9 h-9 w-56"
                  data-testid="group-orders-search"
                />
              </div>
            </div>
          </div>

          {/* Status filter pills */}
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'all', label: 'All', count: orders.length },
              { key: 'awaiting', label: 'Awaiting Approval', count: orders.filter(o => ['submitted', 'quoted'].includes(o.status)).length },
              { key: 'ready', label: 'Ready to Order', count: orders.filter(o => o.status === 'accepted').length },
              { key: 'ordered', label: 'Ordered', count: orders.filter(o => ['ordered', 'shipped', 'in_transit', 'arrived', 'ready_for_collection'].includes(o.status)).length },
              { key: 'delivered', label: 'Delivered', count: orders.filter(o => ['dispatched', 'collected'].includes(o.status)).length },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setStatusFilter(f.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                  statusFilter === f.key
                    ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                    : 'bg-white text-slate-600 border-slate-300 hover:border-blue-400 hover:text-blue-600'
                }`}
                data-testid={`filter-${f.key}`}
              >
                {f.label} <span className="opacity-70">({f.count})</span>
              </button>
            ))}
          </div>

        {/* Info Banner */}
        <Card className="bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Ship className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-900">How Trade Wholesale Quotes Work</h3>
                <p className="text-sm text-blue-700 mt-1">
                  Submit a request for a quote and we'll source it for you, then send the quote back within 24–48 hours.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pending Orders (Quotes) */}
        {pendingOrders.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <Clock className="w-5 h-5 text-purple-600" />
              Pending Quotes ({pendingOrders.length})
            </h2>
            <div className="grid gap-4 md:grid-cols-2">
              {pendingOrders.map(order => (
                <Card key={order.id} className={order.status === 'quoted' ? 'border-purple-300 bg-purple-50/30' : ''}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="font-mono font-semibold text-slate-900">{order.reference_number}</p>
                        {order.job_name && <p className="text-sm font-medium text-indigo-600">{order.job_name}</p>}
                        <p className="text-sm text-slate-500">{order.stone_colour} {order.stone_type}</p>
                      </div>
                      {getStatusBadge(order.status)}
                    </div>
                    
                    <div className="text-sm text-slate-600 mb-3">
                      {order.components?.length || 0} component(s) • Submitted {formatDate(order.created_at)}
                    </div>
                    
                    {order.status === 'quoted' && (
                      <div className="bg-white p-3 rounded-lg border border-purple-200 mb-3">
                        <p className="text-lg font-bold text-purple-700">{formatCurrency(order.quote_amount)}</p>
                        <p className="text-xs text-slate-500">
                          Deposit: {formatCurrency(order.deposit_amount)} • Balance: {formatCurrency(order.balance_amount)}
                        </p>
                        {order.quote_notes && (
                          <p className="text-sm text-slate-600 mt-2 italic">"{order.quote_notes}"</p>
                        )}
                      </div>
                    )}
                    
                    <div className="flex gap-2 flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => openViewDialog(order)}>
                        <Eye className="w-4 h-4 mr-1" />
                        View Details
                      </Button>
                      {order.status === 'quoted' && (
                        <>
                          <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleAcceptQuote(order.id)}>
                            <Check className="w-4 h-4 mr-1" />
                            Accept
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={() => handleDeclineQuote(order.id)}>
                            <X className="w-4 h-4 mr-1" />
                            Decline
                          </Button>
                        </>
                      )}
                      {/* Reorder button */}
                      <Button size="sm" variant="outline" className="text-indigo-600 border-indigo-300" onClick={() => handleReorder(order)}>
                        <RotateCcw className="w-4 h-4 mr-1" />
                        Reorder
                      </Button>
                      {/* Delete button for submitted or cancelled orders */}
                      {canDelete(order.status) && (
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteOrder(order.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                      {/* Cancel button for pre-production orders (not submitted - those can be deleted) */}
                      {canUserCancel(order.status) && order.status !== 'submitted' && (
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleCancelOrder(order.id)}>
                          <X className="w-4 h-4 mr-1" />
                          Cancel
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Active Orders */}
        {activeOrders.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <Truck className="w-5 h-5 text-orange-600" />
              Active Orders ({activeOrders.length})
            </h2>
            <div className="grid gap-4 md:grid-cols-2">
              {activeOrders.map(order => (
                <Card key={order.id} className="border-orange-200">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="font-mono font-semibold text-slate-900">{order.reference_number}</p>
                        <p className="text-sm text-slate-500">{order.stone_colour} {order.stone_type}</p>
                      </div>
                      {getStatusBadge(order.status)}
                    </div>
                    
                    {order.eta && (
                      <div className="bg-orange-50 p-2 rounded mb-3 flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-orange-600" />
                        <span className="text-sm font-medium text-orange-800">ETA: {formatDate(order.eta)}</span>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                      <div>
                        <span className="text-slate-500">Total:</span>
                        <span className="ml-1 font-medium">{formatCurrency(order.quote_amount)}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Deposit:</span>
                        <span className={`ml-1 font-medium ${order.deposit_paid ? 'text-green-600' : 'text-amber-600'}`}>
                          {order.deposit_paid ? '✓ Paid' : 'Pending'}
                        </span>
                      </div>
                    </div>
                    
                    <Button size="sm" variant="outline" onClick={() => openViewDialog(order)}>
                      <Eye className="w-4 h-4 mr-1" />
                      View Details
                    </Button>
                    {/* Reorder button */}
                    <Button size="sm" variant="outline" className="text-indigo-600 border-indigo-300" onClick={() => handleReorder(order)}>
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Reorder
                    </Button>
                    {/* Admin can cancel active orders */}
                    {isAdmin && (
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleCancelOrder(order.id)}>
                        <X className="w-4 h-4 mr-1" />
                        Cancel
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Completed/Cancelled Orders */}
        {completedOrders.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-slate-500" />
              Past Orders ({completedOrders.length})
            </h2>
            <div className="grid gap-4 md:grid-cols-3">
              {completedOrders.map(order => (
                <Card key={order.id} className="bg-slate-50">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-mono text-sm text-slate-600">{order.reference_number}</p>
                        {order.job_name && <p className="text-sm font-medium text-indigo-600">{order.job_name}</p>}
                      </div>
                      {getStatusBadge(order.status)}
                    </div>
                    <p className="text-sm text-slate-500">{order.stone_colour} • {formatCurrency(order.quote_amount)}</p>
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" variant="ghost" onClick={() => openViewDialog(order)}>
                        View Details
                      </Button>
                      {/* Reorder button for past orders */}
                      <Button size="sm" variant="ghost" className="text-indigo-600" onClick={() => handleReorder(order)}>
                        <RotateCcw className="w-4 h-4 mr-1" />
                        Reorder
                      </Button>
                      {/* Delete button for cancelled orders */}
                      {order.status === 'cancelled' && (
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteOrder(order.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {orders.length === 0 && (
          <Card className="text-center py-12">
            <CardContent>
              <Ship className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-xl font-semibold text-slate-600 mb-2">No Group Orders Yet</h3>
              <p className="text-slate-500 mb-4">Submit your first order request to get started with group ordering</p>
              <Button onClick={() => setCreateDialog(true)} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Create Order Request
              </Button>
            </CardContent>
          </Card>
        )}

        {/* No results from filter (but orders exist) */}
        {orders.length > 0 && visibleOrders.length === 0 && (
          <Card className="text-center py-8">
            <CardContent>
              <Search className="w-10 h-10 mx-auto text-slate-300 mb-2" />
              <p className="text-sm text-slate-500">No orders match your search / filter.</p>
              <Button size="sm" variant="link" onClick={() => { setSearchTerm(''); setStatusFilter('all'); }}>
                Clear filters
              </Button>
            </CardContent>
          </Card>
        )}
        </section>
      </div>

      {/* Create Order Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-600" />
              New Group Order Request
            </DialogTitle>
            <DialogDescription>
              Select your memorial type and provide stone details. All measurements in inches.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Linked Memorial Order Banner */}
            {linkedMemorialOrder && (
              <div className="p-4 bg-green-50 rounded-lg border border-green-300 flex items-center gap-3">
                <LinkIcon className="w-5 h-5 text-green-600" />
                <div>
                  <p className="font-medium text-green-800">
                    Linked to Memorial Order #{linkedMemorialOrder.order_number}
                  </p>
                  <p className="text-sm text-green-600">
                    {linkedMemorialOrder.client_name && `Client: ${linkedMemorialOrder.client_name} • `}
                    This order will automatically update the memorial status to "Stone Ordered"
                  </p>
                </div>
              </div>
            )}

            {/* Job Name - Custom naming */}
            <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200 space-y-2">
              <Label className="text-indigo-800 font-medium">Job Name (Optional)</Label>
              <Input
                value={form.job_name}
                onChange={(e) => setForm(prev => ({ ...prev, job_name: e.target.value }))}
                placeholder="e.g., Standard Black Ogee, Smith Family Memorial..."
                className="border-indigo-200 focus:ring-indigo-500"
              />
              <p className="text-xs text-indigo-600">
                Give your job a memorable name for easy reordering later. Must be unique.
              </p>
            </div>

            {/* Request Date - Auto-filled */}
            <div className="p-4 bg-slate-50 rounded-lg border space-y-2">
              <Label className="text-slate-700">Request Date</Label>
              <Input
                value={form.request_date}
                onChange={(e) => setForm(prev => ({ ...prev, request_date: e.target.value }))}
                placeholder="DD MM YYYY"
                className="max-w-[200px] font-mono text-center"
              />
              <p className="text-xs text-slate-500">Format: DD MM YYYY (e.g., 21 07 2026)</p>
            </div>

            {/* Memorial Type Selection */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-4">
              <h3 className="font-semibold text-blue-800">1. Select Memorial Type *</h3>
              <div className="grid grid-cols-2 gap-3">
                {MEMORIAL_TYPES.map(type => (
                  <div
                    key={type.value}
                    onClick={() => handleMemorialTypeChange(type.value)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      form.memorial_type === type.value 
                        ? 'border-blue-500 bg-blue-100' 
                        : 'border-slate-200 bg-white hover:border-blue-300'
                    }`}
                  >
                    <p className="font-medium text-slate-800">{type.label}</p>
                    <p className="text-sm text-slate-500">{type.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Stone Details - Only show after memorial type is selected */}
            {form.memorial_type && (
              <>
                <div className="p-4 bg-slate-50 rounded-lg border space-y-4">
                  <h3 className="font-semibold text-slate-800">2. Stone Details</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Stone Colour *</Label>
                      <select
                        value={form.stone_colour}
                        onChange={(e) => setForm(prev => ({ ...prev, stone_colour: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-md"
                      >
                        <option value="">Select colour...</option>
                        {STONE_COLOURS.map(c => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                      {form.stone_colour === 'Other' && (
                        <Input
                          placeholder="Specify colour..."
                          value={form.custom_colour}
                          onChange={(e) => setForm(prev => ({ ...prev, custom_colour: e.target.value }))}
                        />
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>Stone Type</Label>
                      <select
                        value={form.stone_type}
                        onChange={(e) => setForm(prev => ({ ...prev, stone_type: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-md"
                      >
                        <option value="Granite">Granite</option>
                        <option value="Marble">Marble</option>
                        <option value="Slate">Slate</option>
                        <option value="Limestone">Limestone</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Finish</Label>
                      <select
                        value={form.stone_finish}
                        onChange={(e) => setForm(prev => ({ ...prev, stone_finish: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-md"
                      >
                        {STONE_FINISHES.map(f => (
                          <option key={f} value={f}>{f}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Components */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="font-semibold text-slate-800">3. Components & Dimensions</h3>
                    <div className="flex items-center gap-3">
                      <Label className="text-xs text-slate-600 whitespace-nowrap">Number of sets</Label>
                      <Input
                        type="number"
                        min="1"
                        value={form.set_quantity}
                        onChange={(e) => setForm(prev => ({ ...prev, set_quantity: e.target.value }))}
                        className="w-20 h-8 text-center"
                        data-testid="group-order-set-qty"
                        title="Multiplies every component quantity below (e.g. 5 = 5 of each)"
                      />
                      <Button type="button" size="sm" variant="outline" onClick={addComponent}>
                        <Plus className="w-4 h-4 mr-1" />
                        Add Component
                      </Button>
                    </div>
                  </div>

                  {Number(form.set_quantity) > 1 && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-2 rounded">
                      Multiplier active — each component quantity below will be multiplied by <strong>×{form.set_quantity}</strong> on submit.
                    </p>
                  )}
                  
                  {form.memorial_type === 'headstone' && (
                    <p className="text-sm text-blue-600 bg-blue-50 p-2 rounded">
                      Default sizes pre-filled for headstone (27" × 21" × 3") and base (3" × 24" × 12"). Adjust as needed.
                    </p>
                  )}
                  
                  {form.components.map((comp, idx) => (
                    <div key={idx} className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-blue-800">
                          {COMPONENT_TYPES.find(t => t.value === comp.component_type)?.label || comp.component_type}
                        </span>
                        {form.components.length > 1 && (
                          <Button type="button" size="sm" variant="ghost" className="text-red-600" onClick={() => removeComponent(idx)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label>Type</Label>
                          <select
                            value={comp.component_type}
                            onChange={(e) => updateComponent(idx, 'component_type', e.target.value)}
                            className="w-full px-3 py-2 border rounded-md"
                          >
                            {COMPONENT_TYPES.map(t => (
                              <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="space-y-2">
                          <Label>Quantity</Label>
                          <Input
                            type="number"
                            min="1"
                            value={comp.quantity}
                            onChange={(e) => updateComponent(idx, 'quantity', e.target.value)}
                          />
                        </div>
                      </div>
                      
                      {comp.component_type === 'custom' && (
                        <div className="space-y-2">
                          <Label>Custom Description *</Label>
                          <Input
                            placeholder="Describe the custom component..."
                            value={comp.custom_description}
                            onChange={(e) => updateComponent(idx, 'custom_description', e.target.value)}
                          />
                        </div>
                      )}
                      
                      <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-2">
                          <Label>Height (inches) *</Label>
                          <Input
                            type="number"
                            step="0.25"
                            placeholder="e.g., 24"
                            value={comp.height}
                            onChange={(e) => updateComponent(idx, 'height', e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Width (inches) *</Label>
                          <Input
                            type="number"
                            step="0.25"
                            placeholder="e.g., 18"
                            value={comp.width}
                            onChange={(e) => updateComponent(idx, 'width', e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Depth (inches) *</Label>
                          <Input
                            type="number"
                            step="0.25"
                            placeholder="e.g., 3"
                            value={comp.depth}
                            onChange={(e) => updateComponent(idx, 'depth', e.target.value)}
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Notes (optional)</Label>
                        <Input
                          placeholder="Any special requirements..."
                          value={comp.notes}
                          onChange={(e) => updateComponent(idx, 'notes', e.target.value)}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Additional Notes */}
                <div className="space-y-2">
                  <Label>Additional Notes (optional)</Label>
                  <Textarea
                    placeholder="Any other information about your order..."
                    value={form.customer_notes}
                    onChange={(e) => setForm(prev => ({ ...prev, customer_notes: e.target.value }))}
                    rows={3}
                  />
                </div>

                {/* Link to Memorial Order (optional) */}
                <div className="p-4 bg-slate-50 rounded-lg border space-y-3">
                  <h3 className="font-semibold text-slate-600 text-sm">Link to Memorial Order (optional)</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm">Order Number</Label>
                      <Input
                        placeholder="e.g., 25001"
                        value={form.order_number}
                        onChange={(e) => setForm(prev => ({ ...prev, order_number: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateDialog(false); resetForm(); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={submitting || !form.memorial_type} 
              className="bg-blue-600 hover:bg-blue-700"
            >
              {submitting ? 'Submitting...' : 'Submit Order Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Order Details Dialog */}
      <Dialog open={viewDialog} onOpenChange={setViewDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Order Details
            </DialogTitle>
          </DialogHeader>

          {selectedOrder && (
            <div className="space-y-4 py-4">
              {/* Header */}
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-mono text-lg font-bold text-slate-900">{selectedOrder.reference_number}</p>
                  {selectedOrder.job_name && (
                    <p className="text-sm font-semibold text-indigo-600">{selectedOrder.job_name}</p>
                  )}
                  <p className="text-sm text-slate-500">Submitted {formatDate(selectedOrder.created_at)}</p>
                  {selectedOrder.request_date && (
                    <p className="text-sm text-slate-500">Request Date: {selectedOrder.request_date}</p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2">
                  {getStatusBadge(selectedOrder.status)}
                  {/* Quick Reorder Button */}
                  <Button size="sm" variant="outline" className="text-indigo-600 border-indigo-300" onClick={() => handleReorder(selectedOrder)}>
                    <RotateCcw className="w-4 h-4 mr-1" />
                    Reorder
                  </Button>
                </div>
              </div>

              {/* Stone Details */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-semibold text-slate-700 mb-2">Stone Details</h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Colour:</span>
                    <span className="ml-1 font-medium">{selectedOrder.stone_colour}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Type:</span>
                    <span className="ml-1 font-medium">{selectedOrder.stone_type || '-'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Finish:</span>
                    <span className="ml-1 font-medium">{selectedOrder.stone_finish || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Pallet Delivery */}
              {selectedOrder.pallet_delivery && (
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                    <Truck className="w-4 h-4" />
                    Pallet Delivery
                  </h4>
                  <div className="text-sm">
                    <span className="text-green-700">
                      {selectedOrder.pallet_count} pallet{selectedOrder.pallet_count !== 1 ? 's' : ''} × £{selectedOrder.pallet_cost || 70} = 
                    </span>
                    <span className="ml-1 font-bold text-green-800">
                      £{(selectedOrder.pallet_count || 1) * (selectedOrder.pallet_cost || 70)}
                    </span>
                  </div>
                </div>
              )}

              {/* Components */}
              <div>
                <h4 className="font-semibold text-slate-700 mb-2">Components</h4>
                <div className="space-y-2">
                  {selectedOrder.components?.map((comp, idx) => (
                    <div key={idx} className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                      <div className="flex justify-between">
                        <span className="font-medium text-blue-800">
                          {comp.component_type === 'custom' ? comp.custom_description : 
                            COMPONENT_TYPES.find(t => t.value === comp.component_type)?.label || comp.component_type}
                          <span className="ml-2 inline-block px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-xs font-semibold">× {comp.quantity || 1}</span>
                        </span>
                        <span className="text-sm text-slate-600">
                          {comp.height}" × {comp.width}" × {comp.depth}"
                        </span>
                      </div>
                      {comp.notes && <p className="text-sm text-slate-500 mt-1">{comp.notes}</p>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Quote Details */}
              {selectedOrder.quote_amount && (
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <h4 className="font-semibold text-purple-800 mb-2">Quote Details</h4>
                  <div className="text-2xl font-bold text-purple-700 mb-2">
                    {formatCurrency(selectedOrder.quote_amount)}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-purple-600">Deposit (50%):</span>
                      <span className="ml-1 font-medium">{formatCurrency(selectedOrder.deposit_amount)}</span>
                      {selectedOrder.deposit_paid && <Badge className="ml-2 bg-green-100 text-green-700">Paid</Badge>}
                    </div>
                    <div>
                      <span className="text-purple-600">Balance:</span>
                      <span className="ml-1 font-medium">{formatCurrency(selectedOrder.balance_amount)}</span>
                      {selectedOrder.balance_paid && <Badge className="ml-2 bg-green-100 text-green-700">Paid</Badge>}
                    </div>
                  </div>
                  {selectedOrder.quote_notes && (
                    <p className="text-sm text-slate-600 mt-2 italic">"{selectedOrder.quote_notes}"</p>
                  )}
                  {selectedOrder.quoted_at && (
                    <p className="text-xs text-slate-500 mt-2">Quoted on {formatDate(selectedOrder.quoted_at)}</p>
                  )}
                </div>
              )}

              {/* ETA & Shipping */}
              {(selectedOrder.eta || selectedOrder.ship_name) && (
                <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <h4 className="font-semibold text-orange-800 mb-2">Shipping Information</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {selectedOrder.eta && (
                      <div>
                        <span className="text-orange-600">Expected Arrival:</span>
                        <span className="ml-1 font-medium">{formatDate(selectedOrder.eta)}</span>
                      </div>
                    )}
                    {selectedOrder.ship_name && (
                      <div>
                        <span className="text-orange-600">Ship:</span>
                        <span className="ml-1 font-medium">{selectedOrder.ship_name}</span>
                      </div>
                    )}
                    {selectedOrder.container_number && (
                      <div>
                        <span className="text-orange-600">Container:</span>
                        <span className="ml-1 font-medium">{selectedOrder.container_number}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* ETA History */}
                  {selectedOrder.eta_history?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-orange-200">
                      <p className="text-xs font-medium text-orange-700 mb-2">ETA Changes:</p>
                      <div className="space-y-1">
                        {selectedOrder.eta_history.map((h, i) => (
                          <p key={i} className="text-xs text-slate-600">
                            {formatDate(h.date)}: {formatDate(h.old_eta)} → {formatDate(h.new_eta)}
                            {h.reason && ` (${h.reason})`}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Customer Notes */}
              {selectedOrder.customer_notes && (
                <div className="p-3 bg-slate-100 rounded-lg">
                  <p className="text-sm text-slate-600">{selectedOrder.customer_notes}</p>
                </div>
              )}

              {/* Cancellation Info */}
              {selectedOrder.status === 'cancelled' && selectedOrder.cancelled_at && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm font-medium text-red-800">Order Cancelled</p>
                  <p className="text-xs text-red-600">
                    Cancelled on {formatDate(selectedOrder.cancelled_at)} by {selectedOrder.cancelled_by || 'Unknown'}
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              {selectedOrder.status === 'quoted' && (
                <div className="flex gap-3 pt-4 border-t">
                  <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={() => handleAcceptQuote(selectedOrder.id)}>
                    <Check className="w-4 h-4 mr-2" />
                    Accept Quote
                  </Button>
                  <Button variant="outline" className="flex-1 text-red-600 border-red-300" onClick={() => handleDeclineQuote(selectedOrder.id)}>
                    <X className="w-4 h-4 mr-2" />
                    Decline
                  </Button>
                </div>
              )}

              {/* Dispatch Options - When arrived and ready for dispatch */}
              {selectedOrder.status === 'arrived' && isAdmin && (
                <div className="pt-4 border-t">
                  <h4 className="font-semibold text-slate-700 mb-3">Dispatch Options</h4>
                  <Button 
                    className="w-full bg-lime-600 hover:bg-lime-700"
                    onClick={() => handleOpenDispatch(selectedOrder)}
                    data-testid="arrange-dispatch-btn"
                  >
                    <Truck className="w-4 h-4 mr-2" />
                    Arrange Dispatch / Collection
                  </Button>
                  <p className="text-xs text-slate-500 mt-2 text-center">
                    Items are ready - choose collection or pallet delivery
                  </p>
                </div>
              )}

              {/* Delivery Info - Show if dispatch already set */}
              {(selectedOrder.status === 'ready_for_collection' || selectedOrder.status === 'dispatched') && (
                <div className="pt-4 border-t">
                  <div className={`p-3 rounded-lg ${selectedOrder.delivery_method === 'pallet_delivery' ? 'bg-green-50 border border-green-200' : 'bg-blue-50 border border-blue-200'}`}>
                    <h4 className={`font-semibold ${selectedOrder.delivery_method === 'pallet_delivery' ? 'text-green-800' : 'text-blue-800'} mb-2`}>
                      {selectedOrder.delivery_method === 'pallet_delivery' ? '📦 Pallet Delivery' : '🏪 Collection'}
                    </h4>
                    {selectedOrder.delivery_method === 'pallet_delivery' ? (
                      <div className="text-sm text-green-700 space-y-1">
                        <p>Pallets: {selectedOrder.pallet_count || 1}</p>
                        <p>Cost per pallet: £{selectedOrder.pallet_cost || 70}</p>
                        <p className="font-semibold">Total delivery: £{(selectedOrder.pallet_count || 1) * (selectedOrder.pallet_cost || 70)}</p>
                      </div>
                    ) : (
                      <p className="text-sm text-blue-700">Customer will collect from premises</p>
                    )}
                  </div>
                  
                  {isAdmin && selectedOrder.status === 'ready_for_collection' && (
                    <Button 
                      className="w-full mt-3 bg-slate-600 hover:bg-slate-700"
                      onClick={() => handleMarkCollected(selectedOrder.id)}
                    >
                      Mark as Collected
                    </Button>
                  )}
                </div>
              )}

              {/* Cancel Button - users can cancel before production, admins can cancel anytime */}
              {selectedOrder.status !== 'cancelled' && selectedOrder.status !== 'collected' && (
                (canUserCancel(selectedOrder.status) || isAdmin) && (
                  <div className="pt-4 border-t">
                    <Button 
                      variant="outline" 
                      className="w-full text-red-600 border-red-300 hover:bg-red-50"
                      onClick={() => handleCancelOrder(selectedOrder.id)}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel Order
                      {!isAdmin && !canUserCancel(selectedOrder.status) && ' (Contact Set in Stone)'}
                    </Button>
                    {!isAdmin && (
                      <p className="text-xs text-slate-500 mt-2 text-center">
                        You can cancel before production starts (Submitted, Quoted, or Accepted status)
                      </p>
                    )}
                  </div>
                )
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dispatch Options Dialog */}
      <Dialog open={showDispatchDialog} onOpenChange={setShowDispatchDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-lime-600" />
              Dispatch Options
            </DialogTitle>
            <DialogDescription>
              {dispatchingOrder && `Choose delivery method for order ${dispatchingOrder.job_name || dispatchingOrder.id?.slice(0, 8)}`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Delivery Method Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Delivery Method</Label>
              
              <div 
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  dispatchForm.delivery_method === 'collect' 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-slate-200 hover:border-blue-300'
                }`}
                onClick={() => setDispatchForm(prev => ({ ...prev, delivery_method: 'collect' }))}
                data-testid="dispatch-collect"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    dispatchForm.delivery_method === 'collect' ? 'border-blue-500 bg-blue-500' : 'border-slate-300'
                  }`}>
                    {dispatchForm.delivery_method === 'collect' && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">Customer Collection</p>
                    <p className="text-sm text-slate-600">Customer will collect items from premises</p>
                  </div>
                </div>
              </div>
              
              <div 
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  dispatchForm.delivery_method === 'pallet_delivery' 
                    ? 'border-green-500 bg-green-50' 
                    : 'border-slate-200 hover:border-green-300'
                }`}
                onClick={() => setDispatchForm(prev => ({ ...prev, delivery_method: 'pallet_delivery' }))}
                data-testid="dispatch-pallet"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    dispatchForm.delivery_method === 'pallet_delivery' ? 'border-green-500 bg-green-500' : 'border-slate-300'
                  }`}>
                    {dispatchForm.delivery_method === 'pallet_delivery' && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">Pallet Delivery</p>
                    <p className="text-sm text-slate-600">Arrange pallet delivery to customer</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Pallet Options - Only show if pallet delivery selected */}
            {dispatchForm.delivery_method === 'pallet_delivery' && (
              <div className="p-4 bg-green-50 rounded-lg border border-green-200 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm text-green-800">Number of Pallets</Label>
                    <Input
                      type="number"
                      min="1"
                      value={dispatchForm.pallet_count}
                      onChange={(e) => setDispatchForm(prev => ({ ...prev, pallet_count: e.target.value }))}
                      className="bg-white"
                      data-testid="pallet-count-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-green-800">Cost per Pallet (£)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={dispatchForm.pallet_cost}
                      onChange={(e) => setDispatchForm(prev => ({ ...prev, pallet_cost: e.target.value }))}
                      className="bg-white"
                      data-testid="pallet-cost-input"
                    />
                  </div>
                </div>
                <div className="text-right pt-2 border-t border-green-200">
                  <p className="text-sm text-green-700">
                    Total Delivery Cost: <span className="font-bold text-lg">£{((parseInt(dispatchForm.pallet_count) || 1) * (parseFloat(dispatchForm.pallet_cost) || 70)).toFixed(2)}</span>
                  </p>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDispatchDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmDispatch}
              className={dispatchForm.delivery_method === 'pallet_delivery' ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}
              data-testid="confirm-dispatch-btn"
            >
              {dispatchForm.delivery_method === 'collect' ? 'Mark Ready for Collection' : 'Confirm Pallet Delivery'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default GroupOrdering;
